/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useMemo, useReducer } from "react";
import {
  ModalConfirm,
  Input,
  Loader,
  Box,
  theme,
  FlexContainer,
} from "@nordcloud/gnui";
import {
  useAddOrgUnitMutation,
  BusinessContextDocument,
} from "~/generated/graphql";
import { ReactTable, SortSelector, SortDirection } from "~/components";
import { ERROR_TEXT } from "~/constants";
import { useQueryState } from "~/hooks";
import { useUpdateOrgUnit, useRemoveOrgUnit } from "~/hooks/graphql";
import { showSuccess, showError } from "~/services/toast";
import {
  generateActionConfirmText,
  generateActionSuccessText,
  isEmpty,
} from "~/tools";
import { SELECT_OPTIONS, SortFilter } from "~/views/businessContexts/constants";
import {
  OrganizationUnitForm,
  OrganizationUnitFormData,
} from "~/views/businessContexts/OrganizationUnitForm";
import { EditData, SearchParams, SpendTreeItem } from "../types";
import { OrganizationUnitsTableColumns } from "./OrganizationUnitsTableColumns";
import { reducer, initialState, ACTION } from "./reducer";

type Props = {
  orgUnits: SpendTreeItem[];
  businessContextId: string;
};

export function OrganizationUnitsList({ orgUnits, businessContextId }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    tempOrgUnitData,
    formEditMode,
    initialValues,
    isModalConfirmOpen,
    showForm,
  } = state;
  const {
    set,
    removeFields,
    state: {
      search = "",
      sortField = SortFilter.UnitName,
      sortDirection = SortDirection.Ascending,
    },
  } = useQueryState<SearchParams>();

  const [addOrgUnitV2, { loading: isAddOrgUnitV2Loading }] =
    useAddOrgUnitMutation({
      refetchQueries: [
        {
          query: BusinessContextDocument,
          variables: { id: businessContextId },
        },
      ],
    });

  const [removeOrgUnit, { loading: isRemoveOrgUnitLoading }] =
    useRemoveOrgUnit();

  const [updateOrgUnit, { loading: isUpdateOrgUnitLoading }] =
    useUpdateOrgUnit();

  const onAddHandler = useCallback((parentNid: string) => {
    dispatch({ type: ACTION.ADD, payload: { parentNid } });
  }, []);

  const onEditHandler = useCallback((data: EditData) => {
    dispatch({ type: ACTION.EDIT, payload: { data } });
  }, []);

  const onRemoveHandler = useCallback((nid: string, name: string) => {
    dispatch({ type: ACTION.REMOVE, payload: { name, nid } });
  }, []);

  const handleRemoval = async () => {
    try {
      await removeOrgUnit({ id: tempOrgUnitData.nid ?? "" });
      showSuccess(
        generateActionSuccessText(
          `The ${tempOrgUnitData.name ?? "Organizational Unit"}`
        )()("removed")()
      );
    } catch {
      showError(ERROR_TEXT.default);
    } finally {
      closeModal();
    }
  };

  const handleSearch = (value: string) => {
    if (isEmpty(value)) {
      removeFields(["search"]);
    } else {
      set("search")(value);
    }
  };

  const handleFormSubmit = async (newValues: OrganizationUnitFormData) => {
    try {
      if (formEditMode) {
        await updateOrgUnit({
          nid: tempOrgUnitData.nid ?? "",
          input: newValues,
        });
      } else {
        await addOrgUnitV2({
          variables: {
            input: {
              contextId: businessContextId,
              ...newValues,
              ...tempOrgUnitData,
            },
          },
        });
      }
      showSuccess(
        generateActionSuccessText("Organization Unit")()(
          formEditMode ? "updated" : "created"
        )()
      );
    } catch {
      showError(ERROR_TEXT.default);
    } finally {
      closeForm();
    }
  };

  const closeForm = () => {
    dispatch({ type: ACTION.CLOSE_FORM });
  };

  const closeModal = () => {
    dispatch({ type: ACTION.CLOSE_MODAL });
  };

  const columns = useMemo(
    () =>
      OrganizationUnitsTableColumns({
        structureId: businessContextId,
        onEditHandler,
        onAddHandler,
        onRemoveHandler,
      }),
    [businessContextId, onEditHandler, onAddHandler, onRemoveHandler]
  );

  const isLoading =
    isAddOrgUnitV2Loading || isRemoveOrgUnitLoading || isUpdateOrgUnitLoading;

  return (
    <>
      {isLoading && <Loader isBackground />}
      <Box boxStyle="grey" spacing="spacing04">
        <FlexContainer
          justifyContent="flex-start"
          alignItems="center"
          columnGap={theme.spacing.spacing04}
        >
          <SortSelector
            options={SELECT_OPTIONS}
            value={sortField}
            direction={sortDirection}
            onSelect={(filter) => set("sortField")(filter)}
            onSortChange={(value) => set("sortDirection")(value)}
          />
          <div css={{ flex: 1 }}>
            <Input
              name="search-input"
              placeholder="Search"
              type="search"
              value={search}
              loading={false}
              onChange={(event) => handleSearch(event.target.value)}
            />
          </div>
        </FlexContainer>
      </Box>
      <ReactTable columns={columns} data={orgUnits} />
      <OrganizationUnitForm
        editMode={formEditMode}
        isOpen={showForm}
        initialValues={initialValues}
        onClose={closeForm}
        onSubmit={handleFormSubmit}
      />
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={handleRemoval}
        actionLabel="Delete"
        contentLabel="Delete Organizational Unit"
        data-testid="modal-confirm-orgUnits-list"
        onClose={closeModal}
      >
        {generateActionConfirmText(tempOrgUnitData.name ?? "")("delete")()}
      </ModalConfirm>
    </>
  );
}
